import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { TileCarousel } from 'components/TileCarousel/TileCarousel';
import { UnpublishedListing } from 'components/UnpublishedListing/UnpublishedListing';
import { useFetchFn } from 'hooks/useFetchFn';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { ApiUnpublishedVolop } from 'modules/listing/api/volop/types/ApiUnpublishedVolop';
import { searchAlgoliaFetchRecommendedListings } from 'modules/search/algolia/fetch/searchAlgoliaFetchRecommendedListings';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from 'modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from 'modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetVolopsRoutePath } from 'modules/search/routing/helpers/searchGetVolopsRoutePath';
import { trackListing } from 'utils/analytics/trackListing';

import { getUnpublishedVolopPageSeeMoreText } from './getUnpublishedVolopPageSeeMoreText';

type Props = {
  unpublishedVolop: ApiUnpublishedVolop;
};

export function UnpublishedVolopPage({ unpublishedVolop }: Props) {
  useSetDocumentTitle(getText('Listing Unpublished'));

  const { hasAts, listingName, id, org } = unpublishedVolop;
  useEffect(() => {
    trackListing({
      status: 'unpublished',
      hasAts,
      listingName,
      listingId: id,
      listingType: 'VOLOP',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [hasAts, id, listingName, org]);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedVolop.id,
  });

  const [recommendedListingsResult] = useFetchFn({
    fn: searchAlgoliaFetchRecommendedListings,
    args: [unpublishedVolop],
  });

  const recommendedListings = recommendedListingsResult?.hits || [];

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query });

      navigate(
        searchGetVolopsRoutePath({
          lang: CURRENT_LOCALE(),
          q: parsedQuery,
          filtersByName: { locationType },
        }),
        {
          state: {
            searchFrom: 'Unpublished Volop Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [navigate],
  );

  const seeMoreText = getUnpublishedVolopPageSeeMoreText({
    orgUrl: unpublishedVolop.org
      ? `${unpublishedVolop.org.url[CURRENT_LOCALE()]}#opportunities`
      : undefined,
    orgPublishedVolopsCount: unpublishedVolop.orgPublishedVolopsCount || 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={getText('Search Volunteer Opportunities')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Volop Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        recommendedListings.length > 0 && (
          <TileCarousel
            headline={getText(
              'Explore Similar Volunteer Opportunities on Idealist',
            )}
            results={recommendedListings}
            seeAll={{
              text: getText('See all volunteer opportunities'),
              action: { type: 'button', onClick: () => search('') },
            }}
            listingType="VOLOP"
            carouselLocation="Volop Listing"
            fromListingId={unpublishedVolop.id}
          />
        )
      }
    />
  );
}
